import React from 'react';
import { Box, Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { RADIUS, staticCardSx } from '../../../theme/tokens';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useParams } from 'react-router-dom';
import { useRouteNavigation } from '../../../hooks/useRouteNavigation';

interface ImportantLink {
  title: string;
  href: string;
  icon?: IconName;
  external?: boolean;
}

interface ImportantLinksCardProps {
  title: string;
  subtitle?: string;
  icon: IconName;
  links: ImportantLink[];
}

export default function ImportantLinksCard({
  title,
  subtitle,
  icon,
  links,
}: ImportantLinksCardProps) {
  const navigateWithPrefix = useRouteNavigation();
  const params = useParams<{ state?: string; city?: string }>();

  const getHref = (link: ImportantLink) => {
    if (link.external) return link.href;

    // Gera o href com prefixo se estamos em uma rota com state/city
    const cleanPath = link.href.startsWith('/') ? link.href.substring(1) : link.href;
    if (params.state && params.city) {
      return `/${params.state}/${params.city}/${cleanPath}`;
    }
    return link.href;
  };

  const handleLinkClick = (link: ImportantLink) => (e: React.MouseEvent) => {
    if (!link.external) {
      e.preventDefault();
      navigateWithPrefix(link.href);
    }
    // Para links externos, deixa o comportamento padrão do navegador
  };

  return (
    <Box sx={{ ...(staticCardSx as object), height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Cabeçalho do público */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 48,
            height: 48,
            borderRadius: `${RADIUS.card - 2}px`,
            backgroundColor: 'brand.surface',
            color: 'brand.text',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={icon} style={{ fontSize: '1.25rem' }} />
        </Box>
        <Box>
          <Typography variant="h4" component="h3">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Lista de links */}
      <List sx={{ py: 1, flexGrow: 1 }}>
        {links.map((link, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              component="a"
              href={getHref(link)}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener noreferrer' : undefined}
              onClick={handleLinkClick(link)}
              sx={{
                minHeight: 48,
                px: 3,
                '& .link-arrow': { transition: 'transform 200ms' },
                '&:hover': {
                  backgroundColor: 'brand.surface',
                  '& .MuiListItemText-primary': { color: 'brand.text' },
                  '& .link-arrow': { transform: 'translateX(3px)' },
                },
              }}
            >
              {link.icon && (
                <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                  <FontAwesomeIcon icon={link.icon} style={{ fontSize: '1rem' }} />
                </ListItemIcon>
              )}
              <ListItemText
                primary={link.title}
                primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 500 }}
              />
              {link.external ? (
                <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} aria-label="abre em nova aba" />
              ) : (
                <ArrowForwardIcon className="link-arrow" sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
